import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, Grid2 as Grid, IconButton, Stack, Typography } from "@mui/material";
import { Heart, Image, Link } from "@phosphor-icons/react";
import { GenericFlag } from "../../../components/generic/GenericFlag";
import { CATEGORY_ICONS } from "../../../enums/iconsEnums";
import { GenericIconButton } from "../../../components/generic/GenericIconButton";
import { ProfileInterests } from "../../admin/adminHomePage/profileDetails/ProfileInterests";
import { COLLAB_INTEREST_NSFW, COLLAB_INTEREST_SFW, CONTENT_INTEREST_NSFW, CONTENT_INTEREST_SFW, PENDING_CREATOR_ROLE, PLATFORMS_KEY, SOCIALS_KEY } from "../../../enums/generalEnums";
import { getUserDetails } from "../../../config/apisauce";
import { router, usePage } from "@inertiajs/react";
import { retrieveLastDigitFromURL, shortenNumber, withHTTP } from "../../../helpers/stringHelper";
import { COUNTRIES_WITH_ISO } from "../../../enums/countries";
import { filterArray } from "../../../helpers/arrayHelper";
import { CreatorEvents } from "./CreatorEvents";
import { CollabRequestDialog } from "./CollabRequestDialog";
import { LoginDialog } from "../../../components/ui/topbar/login/LoginDialog";
import { LOGIN_FORMS } from "../../../enums/loginFormEnums";
import { CreatorGalleryDialog } from "./CreatorGalleryDialog";
import useStore from "../../../states/store";
import { useCreateUserPreference } from "../../../components/generic/functions/genericFunctions";
import { SimilarCreators } from "../creators/SimilarCreators";
import { CREATOR_FEED_WIDTH } from "../../../enums/themeEnum";
import { Helmet } from 'react-helmet';
import { COLLAB_REQUEST_STATUS } from "../../../enums/statusEnums";
import ReportCreatorDialog from './../../../components/Dialog/ReportCreatorDialog';

const CreatorDetailsPage = () => {
  const createUserPreference = useCreateUserPreference();
  const [{ userPreference }, { openLoader, closeLoader, setSignUpDialogState }] = useStore();
  const { url, props: { auth } } = usePage();
  const titleProps = { fontSize: '24px', color: "primary", mb: 1.5, fontWeight: 700, variant: 'h2' };
  const [creatorDetails, setCreatorDetails] = useState({});
  const [openRequestCollabDialog, setOpenRequestCollabDialog] = useState(false);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [openGalleryDialog, setOpenGalleryDialog] = useState(false);
  const [isCollabForm, setIsCollabForm] = useState(true);
  const isSelected = userPreference?.saved_profiles?.includes(creatorDetails?.user_id);
  const loggedUser = auth?.user;
  const canCollab = (creatorDetails?.user_id !== loggedUser?.id && loggedUser?.role !== PENDING_CREATOR_ROLE);
  const id = retrieveLastDigitFromURL(url);

  const handleOpenRequestCollabDialog = () => {
    if (auth?.user) {
      setIsCollabForm(true);
      setOpenRequestCollabDialog(true);
    } else {
      setOpenLoginDialog(true);
    }
  };

  const fetchCreatorDetails = async (id) => {
    openLoader();
    const { ok, data } = await getUserDetails({ user_id: id });
    if (ok) {
      setCreatorDetails(data);
    } else {
      router.visit('/');
    }
    closeLoader();
  };


  const socialsAndPlatforms = useMemo(() => {
    return {
      platforms: filterArray(creatorDetails?.user?.category_values, PLATFORMS_KEY, 'category.type') ?? [],
      socials: filterArray(creatorDetails?.user?.category_values, SOCIALS_KEY, 'category.type') ?? []
    };
  }, [creatorDetails]);

  useEffect(() => {
    if (id) {
      fetchCreatorDetails(id);
    } else {
      router.visit('/');
    }
  }, []);

  return (
    <Fragment>
      <Helmet>
        <title>Wanna Collab with {creatorDetails?.user?.name || 'Unknown'}? View Profile </title>
        <meta name="description" content={`Wanna Collab profile for ${creatorDetails?.user?.name}. Connect with ${creatorDetails?.user?.name} for SFW and NSFW collabs`} />
      </Helmet>
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        mb={1}
      >
        <Box sx={{ width: CREATOR_FEED_WIDTH }}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, xsm: 12, mds: 3, sm: 3.6, md: 3.7, lg: 2.9, xl: 2.4, xxl: 2.1 }}>
              <Box
                sx={{
                  borderRadius: '16px 16px 0 0',
                  backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 1) 100%), url(${creatorDetails?.profile_picture?.location})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  width: { sm: '300px' },
                  height: '350px',
                  color: '#FFFF',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    onClick={() => setOpenGalleryDialog(true)}
                    sx={{
                      width: '64px',
                      borderRadius: '16px',
                      backgroundColor: 'primary.main',
                      color: '#FFF',
                      ':hover': {
                        backgroundColor: 'primary.main'
                      }
                    }}>
                    <Image size={16} weight="bold" />
                  </IconButton>
                </Box>
                <Box>
                  <Typography variant="h1" fontSize={'1.25rem'} lineHeight={1.5} fontWeight={700} color="primary" noWrap textTransform={'capitalize'}>{creatorDetails?.user?.name}</Typography>
                  <Typography lineHeight={'16px'}>{creatorDetails?.profession}</Typography>
                </Box>
              </Box>
              <Box sx={{
                width: { sm: '300px' },
                backgroundColor: '#FFF',
                padding: '16px 16px 25px 16px',
                borderRadius: '0 0 16px 16px',
              }}>
                <Box
                  sx={{
                    backgroundColor: 'background.lightBlue',
                    padding: '8px',
                    borderRadius: '16px',
                    mb: 2
                  }}>
                  <Stack direction={'row'} alignItems={'center'} gap={1.5}>
                    <GenericFlag code={COUNTRIES_WITH_ISO?.[creatorDetails?.country]} />
                    <Typography color="text.semiLight">
                      {creatorDetails?.state
                        ? `${creatorDetails?.state}, ${creatorDetails?.country}`
                        : creatorDetails?.country}
                    </Typography>
                  </Stack>
                </Box>
                <Typography fontWeight={800} mb={1.5} color="text.semiLight">PLATFORMS</Typography>
                <Grid container spacing={0.5}>
                  {
                    socialsAndPlatforms?.platforms?.filter((item) => !!item?.value)?.map((platform, index) => {
                      const renderIcon = CATEGORY_ICONS?.[platform?.category?.name] ?? null;
                      const platformValue = withHTTP(platform?.value) ? platform?.value : `https://${platform?.value}`;
                      return (
                        <Grid key={`platform-key-${index}`} item size={6} onClick={() => window.open(platformValue, '_blank')} sx={{ cursor: 'pointer' }}>
                          <Stack direction={'row'} gap={1} alignItems={'center'}>
                            {renderIcon ? <img src={renderIcon} /> : <Link size={20} />}
                            <Typography>{platform?.category?.name}</Typography>
                          </Stack>
                        </Grid>
                      );
                    })
                  }
                </Grid>
                <Typography fontWeight={800} mb={1.5} mt={3} color="text.semiLight">SOCIALS</Typography>
                <Grid container spacing={0.5}>
                  {
                    socialsAndPlatforms?.socials?.filter((item) => !!item?.value)
                      ?.sort((a, b) => (b?.meta?.followerCount ?? 0) - (a?.meta?.followerCount ?? 0))
                      ?.map((social, index) => {
                        const renderIcon = CATEGORY_ICONS?.[social?.category?.name] ?? null;
                        const socialValue = withHTTP(social?.value) ? social?.value : `https://${social?.value}`;
                        return (
                          <Grid key={`social-key-${index}`} item size={4} onClick={() => window.open(socialValue, '_blank')} sx={{ cursor: 'pointer' }}>
                            <Stack direction={'row'} gap={1} alignItems={'center'}>
                              {renderIcon ? <img src={renderIcon} /> : <Link size={20} />}
                              <Typography>{social?.meta?.followerCount ? shortenNumber(social?.meta?.followerCount ?? 0) : <span style={{ fontSize: '12px' }}>{social?.category?.name}</span>}</Typography>
                            </Stack>
                          </Grid>
                        );
                      })
                  }
                </Grid>
                {
                  canCollab && <Stack
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    direction={'row'}
                    gap={1}
                    mt={3}
                  >
                    <GenericIconButton
                      color={isSelected ? 'error.main' : 'grey.dark'}
                      onClick={() => auth?.user ? createUserPreference(isSelected, creatorDetails?.user_id) : setSignUpDialogState(true)}
                      icon={<Heart size={24} weight={isSelected ? 'fill' : 'bold'} />}
                    />
                    {creatorDetails?.latest_collab_request?.status == COLLAB_REQUEST_STATUS.ACCEPTED &&
                      <Button
                        size="large"
                        variant="contained"
                        fullWidth
                        onClick={() => router.visit(`/chats`)}
                      >
                        Message
                      </Button>
                    }

                    {creatorDetails?.latest_collab_request?.status !== COLLAB_REQUEST_STATUS.ACCEPTED &&
                      <Button
                        size="large"
                        variant="contained"
                        fullWidth
                        onClick={() => loggedUser ? handleOpenRequestCollabDialog() : setSignUpDialogState(true)}
                      >
                        Wanna Collab?
                      </Button>
                    }
                  </Stack>
                }
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <ReportCreatorDialog reportedUserId={creatorDetails.user_id} />
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, xsm: 12, mds: 9, sm: 8.4, md: 8.3, lg: 9.1, xl: 9.6, xxl: 9.9 }}>
              <Stack gap={3}>
                <Card sx={{ minHeight: { xs: 'auto', sm: '363px' } }}>
                  <Typography {...titleProps}>About Me</Typography>
                  <Typography variant="subtitle1">{creatorDetails?.bio}</Typography>
                </Card>
                <ProfileInterests
                  titleProps={titleProps}
                  title={'Collab Interest'}
                  interests={creatorDetails?.user?.collab_interest}
                  sfwKey={COLLAB_INTEREST_SFW}
                  nsfwKey={COLLAB_INTEREST_NSFW}
                />
                <ProfileInterests
                  titleProps={titleProps}
                  title={'Content Interest'}
                  interests={creatorDetails?.user?.content_interest}
                  sfwKey={CONTENT_INTEREST_SFW}
                  nsfwKey={CONTENT_INTEREST_NSFW}
                />
              </Stack>
            </Grid>
          </Grid>
          <CreatorEvents
            hasLoggedUser={loggedUser}
            currentUser={loggedUser?.id === creatorDetails?.user_id}
            canCollab={canCollab}
            handleOpenRequestCollabDialog={() => handleOpenRequestCollabDialog()}
          />
        </Box>
      </Box>
      <SimilarCreators
        selectedCreatorId={id}
        tags={[
          ...socialsAndPlatforms.platforms.map((item) => item?.category?.name),
          ...socialsAndPlatforms.socials.map((item) => item?.category?.name),
        ]}
        contentInterest={creatorDetails?.user?.content_interest?.map((item) => item?.name)}
        collabInterest={creatorDetails?.user?.collab_interest?.map((item) => item?.name)}
      />
      <CollabRequestDialog
        creatorDetails={creatorDetails?.user}
        isCollabForm={isCollabForm}
        setIsCollabForm={setIsCollabForm}
        openRequestCollabDialog={openRequestCollabDialog}
        handleCloseRequestCollabDialog={() => setOpenRequestCollabDialog(false)}
      />
      <LoginDialog
        selectedForm={LOGIN_FORMS[0]}
        openLoginDialog={openLoginDialog}
        handleCloseDialog={() => setOpenLoginDialog(false)}
      />
      <CreatorGalleryDialog
        creatorDetails={creatorDetails}
        openGalleryDialog={openGalleryDialog}
        handleCloseGalleryDialog={() => setOpenGalleryDialog(false)}
      />
    </Fragment >
  );
};

export default CreatorDetailsPage;